"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ShieldPlus,
  HeartPulse,
  FlaskConical,
  Activity,
  Users,
  Pill,
  ArrowRight,
} from "lucide-react";

export default function Home() {
  const t = useTranslations("landing");
  const tc = useTranslations("common");

  const features = [
    {
      href: "/profile",
      icon: HeartPulse,
      title: t("featureProfile"),
      description: t("featureProfileDesc"),
      accent: "from-blue-500/20 to-blue-600/5",
      iconColor: "text-blue-600",
    },
    {
      href: "/safety/interactions",
      icon: FlaskConical,
      title: t("featureInteractions"),
      description: t("featureInteractionsDesc"),
      accent: "from-slate-500/15 to-slate-600/5",
      iconColor: "text-slate-600 dark:text-slate-300",
    },
    {
      href: "/safety/regimen",
      icon: Activity,
      title: t("featureRegimen"),
      description: t("featureRegimenDesc"),
      accent: "from-emerald-500/20 to-emerald-600/5",
      iconColor: "text-emerald-600",
    },
    {
      href: "/catalog",
      icon: Pill,
      title: t("featureCatalog"),
      description: t("featureCatalogDesc"),
      accent: "from-rose-500/20 to-rose-600/5",
      iconColor: "text-rose-600",
    },
    {
      href: "/profile/access",
      icon: Users,
      title: t("featureSharing"),
      description: t("featureSharingDesc"),
      accent: "from-violet-500/20 to-violet-600/5",
      iconColor: "text-violet-600",
    },
  ];

  return (
    <div className="flex min-h-screen flex-col">
      {/* Nav */}
      <header className="border-b bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <ShieldPlus className="h-6 w-6 text-primary" />
            <span className="text-base font-semibold tracking-tight">
              {tc("appName")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" nativeButton={false} render={<Link href="/login" />}>
              {t("signIn")}
            </Button>
            <Button size="sm" nativeButton={false} render={<Link href="/register" />}>
              {t("createAccount")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Subtle grid background */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        {/* Gradient glow */}
        <div className="absolute -top-24 left-1/2 h-96 w-[600px] -translate-x-1/2 rounded-full bg-primary/8 blur-3xl" />

        <div className="relative mx-auto max-w-5xl px-4 pb-20 pt-24 sm:pb-28 sm:pt-32">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border bg-background/80 px-4 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur-sm">
              <ShieldPlus className="h-3.5 w-3.5 text-primary" />
              {tc("appName")}
            </div>

            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              {t("heroTitle")}
            </h1>

            <p className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
              {t("heroSubtitle")}
            </p>

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button size="lg" nativeButton={false} render={<Link href="/register" />}>
                {t("createAccount")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" size="lg" nativeButton={false} render={<Link href="/login" />}>
                {t("signIn")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t bg-muted/30">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:py-24">
          <div className="grid gap-4 sm:grid-cols-2">
            {features.map(({ href, icon: Icon, title, description, accent, iconColor }, i) => (
              <Link
                key={href}
                href={href}
                className={cn(
                  "group relative rounded-xl border bg-background p-6 transition-shadow hover:shadow-md",
                  i === features.length - 1 && features.length % 2 !== 0 &&
                    "sm:col-span-2 sm:mx-auto sm:max-w-[calc(50%-0.5rem)]",
                )}
              >
                <div
                  className={`absolute inset-0 rounded-xl bg-gradient-to-br ${accent} opacity-0 transition-opacity group-hover:opacity-100`}
                />
                <div className="relative">
                  <div
                    className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-muted ${iconColor}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-semibold tracking-tight">
                    {title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center sm:py-20">
          <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
            {t("heroTitle")}
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
            {t("heroSubtitle")}
          </p>
          <div className="mt-6">
            <Button size="lg" nativeButton={false} render={<Link href="/register" />}>
              {t("createAccount")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto border-t bg-muted/30">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldPlus className="h-4 w-4" />
            {tc("appName")}
          </div>
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
}
